import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import {
    createAcademicYear,
    fetchAcademicYears,
    saveTermBatch,
    setCurrentPeriod,
    syncFromCalendarNotices,
    updateTermStatus,
} from "../../../utils/academicPeriodApi";
import { defaultAcademicYearMeta, displayTermStatus } from "../../../utils/currentTermUtils";
import { getSchoolIdForUser } from "../../../utils/userContext";

const TERM_ROWS = [
    { termNumber: 1, term: "Term 1" },
    { termNumber: 2, term: "Term 2" },
    { termNumber: 3, term: "Term 3" },
];

export default function AcademicYearManagement() {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const schoolType = currentUser?.schoolType || "Secondary";

    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [messageSeverity, setMessageSeverity] = useState("info");
    const [selectedYearId, setSelectedYearId] = useState("");
    const [educationLevel, setEducationLevel] = useState(
        schoolType === "Primary" ? "Primary" : schoolType === "Combined" ? "All" : "Secondary"
    );
    const [termForms, setTermForms] = useState({});
    const [saving, setSaving] = useState(false);
    const [newYearOpen, setNewYearOpen] = useState(false);
    const [newYearLabel, setNewYearLabel] = useState(defaultAcademicYearMeta().label);

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const list = await fetchAcademicYears(schoolId);
            setYears(Array.isArray(list) ? list : []);
            setMessage("");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Could not load academic years.");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const selectedYear = years.find((y) => (y._id || y.id) === selectedYearId) || years[0];

    useEffect(() => {
        if (selectedYear && !selectedYearId) {
            setSelectedYearId(selectedYear._id || selectedYear.id);
        }
    }, [selectedYear, selectedYearId]);

    useEffect(() => {
        if (!selectedYear?.terms) {
            setTermForms({});
            return;
        }
        const map = {};
        for (const t of selectedYear.terms) {
            map[t.termNumber] = {
                openDate: t.openDate || "",
                closeDate: t.closeDate || "",
                note: t.note || "",
                status: t.status || "Planned",
                id: t._id || t.id,
            };
        }
        setTermForms(map);
    }, [selectedYear]);

    const handleSaveTerms = async () => {
        if (!schoolId || !selectedYear) return;
        setSaving(true);
        setMessage("");
        try {
            const terms = TERM_ROWS.map((row) => ({
                termNumber: row.termNumber,
                term: row.term,
                openDate: termForms[row.termNumber]?.openDate || "",
                closeDate: termForms[row.termNumber]?.closeDate || "",
                note: termForms[row.termNumber]?.note || "",
                status: termForms[row.termNumber]?.status || "Planned",
            }));
            await saveTermBatch(schoolId, {
                academicYear: selectedYear.label,
                educationLevel: selectedYear.educationLevel || educationLevel,
                terms,
            });
            await load();
            setMessageSeverity("success");
            setMessage("Term dates saved.");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleOpenTerm = async (termId) => {
        try {
            await updateTermStatus(termId, "Open");
            await load();
            setMessageSeverity("success");
            setMessage("Term opened and set as current.");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Could not open term.");
        }
    };

    const handleCloseTerm = async (termId) => {
        try {
            await updateTermStatus(termId, "Closed");
            await load();
            setMessageSeverity("success");
            setMessage("Term closed.");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Could not close term.");
        }
    };

    const handleSetCurrent = async (yearId, termId) => {
        try {
            await setCurrentPeriod(schoolId, { academicYearId: yearId, schoolTermId: termId });
            setMessageSeverity("success");
            setMessage("Current period updated.");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Could not set current period.");
        }
    };

    const handleCreateYear = async () => {
        try {
            await createAcademicYear(schoolId, { label: newYearLabel, educationLevel });
            setNewYearOpen(false);
            await load();
            setMessageSeverity("success");
            setMessage("Academic year created.");
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Could not create year.");
        }
    };

    const handleSync = async () => {
        try {
            const result = await syncFromCalendarNotices(schoolId);
            await load();
            setMessageSeverity("success");
            setMessage(`Synced ${result.synced || 0} term(s) from existing calendar.`);
        } catch (err) {
            setMessageSeverity("error");
            setMessage(err.response?.data?.message || "Sync failed.");
        }
    };

    if (loading && years.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="overline" color="text.secondary">
                        Administration
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                        Academic years & terms
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Create academic years, set term open/close dates, and open or close terms for the whole school.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button startIcon={<SyncIcon />} variant="outlined" onClick={handleSync}>
                        Sync from calendar
                    </Button>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => setNewYearOpen(true)}>
                        New academic year
                    </Button>
                </Stack>
            </Stack>

            {message ? (
                <Alert severity={messageSeverity} sx={{ mb: 2 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            ) : null}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Academic year</TableCell>
                            <TableCell>Section</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Terms</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {years.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                        No academic years yet. Create one or sync from the existing school calendar.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            years.map((y) => (
                                <TableRow
                                    key={y._id || y.id}
                                    selected={(y._id || y.id) === (selectedYear?._id || selectedYear?.id)}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => setSelectedYearId(y._id || y.id)}
                                >
                                    <TableCell sx={{ fontWeight: 600 }}>{y.label}</TableCell>
                                    <TableCell>{y.educationLevel || "All"}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={y.status || "Planned"} variant="outlined" />
                                    </TableCell>
                                    <TableCell>{(y.terms || []).length}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={(e) => { e.stopPropagation(); setSelectedYearId(y._id || y.id); }}>
                                            Edit terms
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Paper>

            {selectedYear ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        {selectedYear.label} — term configuration
                    </Typography>
                    <Grid container spacing={2}>
                        {TERM_ROWS.map((row) => {
                            const tf = termForms[row.termNumber] || {};
                            const termId = tf.id;
                            const statusDisplay = displayTermStatus({
                                status: tf.status,
                                openDate: tf.openDate,
                                closeDate: tf.closeDate,
                            });
                            return (
                                <Grid item xs={12} key={row.termNumber}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: (t) => alpha(t.palette.primary.main, 0.02) }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                            <Typography fontWeight={700}>{row.term}</Typography>
                                            <Chip
                                                size="small"
                                                label={statusDisplay.label}
                                                color={statusDisplay.color}
                                                variant={statusDisplay.variant}
                                            />
                                        </Stack>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Open date"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={tf.openDate || ""}
                                                    onChange={(e) =>
                                                        setTermForms((prev) => ({
                                                            ...prev,
                                                            [row.termNumber]: { ...prev[row.termNumber], openDate: e.target.value },
                                                        }))
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Close date"
                                                    type="date"
                                                    InputLabelProps={{ shrink: true }}
                                                    value={tf.closeDate || ""}
                                                    onChange={(e) =>
                                                        setTermForms((prev) => ({
                                                            ...prev,
                                                            [row.termNumber]: { ...prev[row.termNumber], closeDate: e.target.value },
                                                        }))
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <FormControl fullWidth size="small">
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        label="Status"
                                                        value={tf.status || "Planned"}
                                                        onChange={(e) =>
                                                            setTermForms((prev) => ({
                                                                ...prev,
                                                                [row.termNumber]: { ...prev[row.termNumber], status: e.target.value },
                                                            }))
                                                        }
                                                    >
                                                        <MenuItem value="Planned">Planned</MenuItem>
                                                        <MenuItem value="Open">Open</MenuItem>
                                                        <MenuItem value="Closed">Closed</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                        {termId ? (
                                            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                                                <Button size="small" variant="contained" onClick={() => handleOpenTerm(termId)}>
                                                    Open term
                                                </Button>
                                                <Button size="small" variant="outlined" onClick={() => handleCloseTerm(termId)}>
                                                    Close term
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => handleSetCurrent(selectedYear._id || selectedYear.id, termId)}
                                                >
                                                    Set as current
                                                </Button>
                                            </Stack>
                                        ) : null}
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button variant="contained" disabled={saving} onClick={handleSaveTerms}>
                            {saving ? "Saving…" : "Save all term dates"}
                        </Button>
                    </Stack>
                </Paper>
            ) : null}

            <Dialog open={newYearOpen} onClose={() => setNewYearOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>New academic year</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Year label"
                            value={newYearLabel}
                            onChange={(e) => setNewYearLabel(e.target.value)}
                            placeholder="2025/2026"
                        />
                        {schoolType === "Combined" ? (
                            <FormControl fullWidth>
                                <InputLabel>Section</InputLabel>
                                <Select label="Section" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}>
                                    <MenuItem value="All">All sections</MenuItem>
                                    <MenuItem value="Primary">Primary</MenuItem>
                                    <MenuItem value="Secondary">Secondary</MenuItem>
                                </Select>
                            </FormControl>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewYearOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateYear}>
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
